
export enum UserRole {
  // OWNER (2026-10-17): everything SUPER_ADMIN has, plus the full customer list and the right to make
  // or change super admins and owners. Migration 0134 adds the enum value.
  OWNER = 'owner',
  SUPER_ADMIN = 'super_admin',
  ADMIN = 'admin',
  DISPATCH = 'dispatch',
  STORE = 'store',
  DRIVER = 'driver',
  WAREHOUSE_MANAGER = 'warehouse_manager',
  // WORKER: performs the SKU transformation (bulk → finished product) against
  // their LotAssignment. Records every run, crumbs generated, waste. Holds
  // their own bulk / crumbs / leftovers until consumed or returned.
  WORKER = 'worker',
  // PACKAGER: QA / handoff role. Confirms finished products handed over by the
  // Worker. Only the confirm event credits Product.warehouseStock.
  PACKAGER = 'packager',
  // STOCK_MANAGER (was 'manager' until 2026-09-10): product catalog (products
  // CRUD + CSV import) + read-only Stock Overview. Deliberately has ZERO order
  // visibility: no order routes, no order listeners, and orders reads are
  // denied in firestore.rules / Postgres RLS (is_manager()).
  STOCK_MANAGER = 'stock_manager',
  // MANAGER (new, 2026-09-10): operations manager. Dashboard, Warehouse,
  // Packaging House, Products, Warehouse History, Store / Warehouse Reports,
  // Store Handoffs, Inventory Ops, Inventory Tracker, Stock Overview,
  // Settings — same behaviour on each page as Admin, minus Orders, Chat,
  // Admin Panel and Financial Reports. Backed by the dispatch + warehouse RLS
  // tiers in Postgres (migration 0047) and isDispatch()/isWarehouseManager()
  // in firestore.rules.
  MANAGER = 'manager',
  // TEAM_MEMBER (2026-09-24): a person on the team who only works the Task
  // Management board — no orders, stock, chat or reports. Sidebar shows the
  // board (and Settings for their own password); RLS reaches them only
  // through authed() policies (the task tables, users for the assignee list).
  // Migration 0093 adds the enum value.
  TEAM_MEMBER = 'team_member',
}

export interface User {
  id: string;
  name: string;
  role: UserRole;
  email: string;
  phone?: string;
  address?: string;
  password?: string;
  requiresPasswordReset?: boolean;
  isArchived?: boolean;
  // Job title shown beside the name (Admin Panel, migration 0093). Free text, optional.
  designation?: string;
  // Migration stand-in for a driver/store deleted from Firestore long ago
  // (users.placeholder, see 0020_legacy_tracking.sql). Always archived, never
  // a login; exists only so old orders/stock keep a name. Hidden from every
  // user list — it is not an account anyone manages.
  placeholder?: boolean;
  // Store-level temp packer — LEGACY single slot. Since migration 0050 the
  // managers live in `store_temp_managers` (StoreTempManager[]); this field
  // is a DB-maintained mirror of the most recent assignment kept for old
  // builds. Read through services/tempManagers.ts, never directly.
  tempPackerDriverId?: string;
  // Drivers only (0084): may this driver be made a temporary store manager? Existing drivers
  // have it; new drivers start without it. Absent (older rows, pre-0084 databases) = has access —
  // read it through services/storeAccess.ts#hasStoreAccess, never `=== true`.
  storeAccess?: boolean;
  // Notification tracking: timestamps of when driver last read each order / chat channel
  lastReadOrders?: Record<string, number>;      // { [orderId]: timestamp }
  lastReadChat?: Record<string, Record<string, number>>; // { [orderId]: { [channel]: timestamp } }
  // Tab-level unread: timestamp of the last time the user opened each tab.
  // Items newer than this cut-off drive the bottom-tab / nav badge counts.
  // Per-role usage:
  //   driver: deliveries / pickups / transfers / store (temp-packer)
  //   store : orders   (new orders assigned to this store)
  //   admin : orders   (new orders system-wide)
  lastOpenedAt?: {
    deliveries?: number;
    pickups?: number;
    transfers?: number;
    store?: number;
    orders?: number;
    /** Set when the user opens the Messages tab. Drives the tab-bar
     *  badge so it shows ONLY conversations whose last message landed
     *  after this timestamp ("new since I last visited the tab"). */
    messages?: number;
  };
  // Mobile-only: FCM push tokens written here on login from the mobile app.
  // The web app does not write or read this field.
  fcmTokens?: string[];
  // Text Inbox per-thread read marks: { [normalized phone]: lastReadMs }.
  // Written by the user's own session (web NotificationHandler) so unread
  // counts agree across every browser/device — localStorage is only a
  // per-browser fallback. Self-updates pass the existing users rules.
  textReadMarks?: Record<string, number>;
  // Whole-inbox "everything before this is read" floor for the Text Inbox —
  // the legacy bulk-read cut-off, lifted from localStorage to the account so
  // every device a role signs into agrees on historical read state.
  textReadFloor?: number;
  // Twilio number assigned to this driver for outbound SMS/calls (E.164, e.g. "+16137038761")
  twilioNumber?: string;
  // ─── Live driver location (driver role only) ──────────────────────────────
  // Written by the mobile app's expo-location foreground service while the
  // driver is on duty. Read by the DeliveryMap dispatch view to render live
  // driver markers. Off-duty drivers retain their last lastLocation until a
  // scheduled cloud function purges it after 14 days.
  isOnDuty?: boolean;
  lastLocation?: {
    lat: number;
    lng: number;
    accuracy?: number;
    speed?: number | null;
    heading?: number | null;
    updatedAt: number;
  };
  // ─── GPS control (UPE-119, migration 0090; driver role only) ────────────────
  // Written by ADMINS from the Driver Control page: gpsExempt = this driver may
  // work with GPS off; outOfShift = the admin ended the shift (map + assign
  // picker mark them, the phone stops tracking). gpsOnAt is written by the
  // phone: last time it saw GPS on while working — "working today" on the panel.
  gpsExempt?: boolean;
  outOfShift?: boolean;
  outOfShiftAt?: number;    // ms since epoch
  outOfShiftBy?: string;    // admin name
  gpsOnAt?: number;         // ms since epoch
}

/** One row of `store_temp_managers`: a driver acting as temporary manager of
 *  a store (several per store since 0050). Mobile keeps a copy in
 *  types/index.ts. */
export interface StoreTempManager {
  storeId: string;
  driverId: string;
  assignedBy?: string | null;
  assignedAt: number;            // ms epoch
}

/** A Packing House (migration 0080): the place a lot is handed to. Its
 *  members share ONE pool of material; every run still names the packager
 *  who made it. Admins create houses in the Admin Panel. */
export interface PackingHouse {
  id: string;
  name: string;
  address?: string;
  notes?: string;
  isActive: boolean;
  createdAt: number;             // ms epoch
  createdBy?: string | null;
}

/** One row of `packing_house_members`: a packager working in a house. A
 *  packager may belong to several houses (Umer, 2026-09-22). */
export interface PackingHouseMember {
  houseId: string;
  userId: string;
  assignedBy?: string | null;
  assignedAt: number;            // ms epoch
}

export interface TwilioNumber {
  id: string;                  // doc id (same as number without +)
  number: string;              // E.164 format, e.g. "+16137038761"
  label?: string;              // optional display label
  assignedDriverId?: string | null;
  isDefault?: boolean;         // fallback for drivers without an assigned number
  addedAt: number;
  addedBy?: string;
}

export interface Product {
  id: string;
  name: string;
  sku?: string;
  category: string;
  price: number;
  warehouseStock: number;
  isActive?: boolean;
  createdAt?: number;                 // Unix ms — set on manual add and CSV import. Optional so legacy products still validate.
  // What ONE package is (0112, 2026-10-05) — services/productUnits. Both set or both absent; absent = "No unit"
  // (tagged in the Storage House). Stock stays a count of packages; this only describes the package.
  unitAmount?: number;
  unitKind?: ProductUnitKind;
}

/** Weight is held in grams (shown as oz / lb by the shop's 28 g / 448 g rule); 'each' = items (1 = single, N = a pack of N). */
export type ProductUnitKind = 'g' | 'mg' | 'ml' | 'each';

export interface StoreStock {
  storeId: string;
  productId: string;
  quantity: number;
}

export interface DriverStock {
  driverId: string;
  productId: string;
  quantity: number;
}

export enum TransactionType {
  ASSIGN_TO_STORE = 'assign',
  RETURN_FROM_STORE = 'return',
  CASH_TURN_IN = 'cash_turn_in',
  ASSIGN_TO_DRIVER = 'assign_to_driver',
  RETURN_FROM_DRIVER = 'return_from_driver',
  DRIVER_TO_DRIVER = 'driver_to_driver',
  STORE_TO_DRIVER = 'store_to_driver',
  DRIVER_TO_STORE = 'driver_to_store',
}

export enum TransactionStatus {
  PENDING_STORE = 'pending_store',
  PENDING_DISPATCH = 'pending_dispatch',
  PENDING_ADMIN = 'pending_admin',
  PENDING_DRIVER = 'pending_driver',
  APPROVED = 'approved',
  REJECTED = 'rejected'
}

export interface StockTransaction {
  id: string;
  type: TransactionType;
  status: TransactionStatus;
  storeId: string;
  driverId?: string;
  fromDriverId?: string;
  toDriverId?: string;
  // `receivedQuantity` is stamped by approve_transaction when the receiver
  // counted a different number than was sent (see TransactionReceipt).
  items: { productId: string; quantity: number; receivedQuantity?: number }[];
  cashAmount?: number;
  timestamp: number;
  // Attribution: who assembled/initiated this transaction. Optional for
  // backward compatibility with legacy docs. Populated by the Packager
  // bundle flow so audit can distinguish packager-initiated bundles from
  // dispatch-initiated ones after the store approves.
  createdBy?: {
    userId: string;
    userName: string;
    role: UserRole;
  };
  // Direct warehouse assignment (migration 0056): the production run whose
  // packs this transfer carries. Absent on Inventory Ops transfers — that is
  // how history tells the two routes apart.
  sourceRunId?: string;
  // Set by approve_transaction when the receiver's counts differed from what
  // was sent. The difference stays on the warehouse balance until dispatch
  // resolves it; Inventory Ops lists flagged transactions for review.
  discrepancy?: boolean;
  discrepancyNote?: string;
}

// What a store / driver hands back when confirming receipt of a transfer.
// `received` maps productId → units actually counted; products left out are
// taken as received in full. Only sent when at least one count differs.
export interface TransactionReceipt {
  received: Record<string, number>;
  note?: string;
}

export enum OrderStatus {
  PENDING = 'pending',
  ASSIGNED = 'assigned',
  CONFIRMED = 'confirmed',
  PICKED_UP = 'picked_up',
  DELIVERED = 'delivered',
  RETURNING_TO_STORE = 'returning_to_store',
  // Soft delete (0097, 2026-09-25): the order is kept, hidden from active lists, and can be put back.
  // Only a Pending order can be cancelled. Its board card moves to Cancelled with it.
  CANCELLED = 'cancelled'
}

export interface OrderItem {
  productId: string;
  quantity: number;
}

export interface Order {
  id: string;
  orderNumber?: string;
  customerName: string;
  address: string;
  // Address verification (Google Geocoding). All optional and
  // backward-compatible — legacy orders simply have these undefined.
  // `addressVerified` true means the address was confirmed against Google
  // and must NOT be re-checked. `addressKept` true means the admin saw a
  // correction suggestion and chose to keep their own address as-is — also
  // a settled state, so we never re-suggest for it.
  addressVerified?: boolean;
  addressKept?: boolean;
  formattedAddress?: string;
  addressLat?: number;
  addressLng?: number;
  addressPlaceId?: string;
  items: OrderItem[];
  status: OrderStatus;
  storeId?: string;
  driverId?: string;

  // Payment Details
  requiresCash: boolean;
  cashAmount: number;
  requiresETransfer: boolean;
  eTransferAmount: number;

  // Bags
  bags?: number;

  // Priority — free-form admin-typed string (e.g. "A1", "B5", "1").
  // Capped at 5 chars, uppercased + trimmed at write time. Legacy data
  // may contain numbers; render via the local `normalizePriority` helper
  // which coerces both shapes to a display string.
  priority?: string;

  // Store pickup confirmation
  storeConfirmedPickup?: boolean;

  // How the order reaches the customer. Absent/'delivery' = local driver
  // run (all legacy orders). 'shipping' = city-to-city mail shipment: a
  // store packs and mails it, so no driver is ever assigned.
  orderType?: 'delivery' | 'shipping';

  // Delivery Config
  requirePhoto?: boolean;

  // Call agent integration; 'bot' = made by DispatcherBot from its board card (0127, 2026-10-10)
  source?: 'manual' | 'call_agent' | 'bot';
  callerPhone?: string;
  callId?: string;

  // Customer messaging
  customerPhone?: string;
  // Mail-order flow: when set, order creation emails the order details to the
  // team mailbox (staging test) and the payment-approval emails can reach the
  // customer. Local/driver orders leave it empty.
  customerEmail?: string;

  // Created from an Order Board card (Orders → New Order → "From Order Board", migration 0095):
  // the card's id, and that card's order number (set by the database). Written once, on create;
  // never edited.
  boardCardId?: string;
  boardOrderNumber?: string;
  // Night Time Deliveries (0096): the order was confirmed from a night card. Set by the database only;
  // shown as a NIGHT chip on the map, the Orders list and the driver page.
  isNight?: boolean;
  // Soft delete (0097): when and by whom the order was cancelled; cleared by Put back.
  cancelledAt?: number;
  cancelledBy?: string;

  notes?: string;
  createdAt: number;
  deliveredAt?: number;
  /** 0105: the office user who marked it delivered (Mark delivered) — empty when its driver did. Written by the database path only. */
  deliveredBy?: string;
  deliveryPhoto?: string | null;
  handoffPhoto?: string | null;
  handoffPhotos?: string[];

  // Driver return confirmation
  driverReturnConfirmed?: boolean;

  // Dispatch user who assigned this order
  dispatchId?: string;

  // Dispatch/admin user who originally CREATED this order. Drives per-dispatch
  // order isolation: a dispatch sees orders they created or claimed. Absent on
  // legacy orders and call-agent (inbound) orders, which stay a shared queue
  // until a dispatch claims one by assigning it.
  createdBy?: string;

  // Driver-to-driver transfer
  transferPending?: boolean;
  transferFromDriverId?: string;
  transferToDriverId?: string;
  transferFromApproved?: boolean;
  transferToApproved?: boolean;
  /** Set the moment a transfer is fully approved (both sides agreed) and
   *  the order moves to its new driver. Used by the receiving driver's
   *  Deliveries tab badge to detect "this just landed in my route" since
   *  `createdAt` is the original admin-placed timestamp and won't fire. */
  transferApprovedAt?: number;

  // Fulfilled directly from driver stock (no store involved)
  fulfilledFromDriver?: boolean;
}

// Per-pair order chat channels. Strict pair privacy:
//   customer      = admin/driver ↔ customer (SMS). Driver is pushed; admin
//                   can send but isn't pushed on replies.
//   dispatch      = admin ↔ driver (in-app)
//   admin_store   = admin ↔ store (in-app)
//   driver_store  = driver ↔ store (in-app). No admin observer.
// Non-participants can audit via the drill-in UI (read-only), but never
// receive push / badges / unread counts for pairs they're not in.
export type MessageChannel = 'customer' | 'dispatch' | 'admin_store' | 'driver_store';

// Per-pair tabs a given viewer should see for an order. Admin's main tab
// bar deliberately does NOT include driver_store — admin still gets pushed
// on it and can drill in via the order detail.
export const getOrderChatChannels = (
  role: UserRole | string | undefined,
  hasStore: boolean,
  hasDriver: boolean,
): MessageChannel[] => {
  const r = role as UserRole;
  if (r === UserRole.DRIVER) {
    return hasStore
      ? ['dispatch', 'driver_store', 'customer']
      : ['dispatch', 'customer'];
  }
  if (r === UserRole.STORE) {
    return hasDriver
      ? ['admin_store', 'driver_store']
      : ['admin_store'];
  }
  // super_admin / admin / dispatch
  return hasStore
    ? ['dispatch', 'admin_store', 'customer']
    : ['dispatch', 'customer'];
};

// All channels that could hold unread counts for any viewer. Used by
// unread-tracking code that iterates channels regardless of role.
export const ALL_ORDER_CHANNELS: MessageChannel[] = [
  'dispatch',
  'admin_store',
  'driver_store',
  'customer',
];

// Channels this viewer can DRILL IN to audit read-only — threads they're
// not a participant of but may need to inspect (dispute resolution, etc.).
// Hidden by default behind a drill-in toggle inside the chat modal.
export const getDrillInChannels = (
  role: UserRole | string | undefined,
  hasStore: boolean,
  hasDriver: boolean,
): MessageChannel[] => {
  const r = role as UserRole;
  if (r === UserRole.DRIVER) {
    // Driver can peek at admin↔store chatter (bag prep issues, etc.)
    return hasStore ? ['admin_store'] : [];
  }
  if (r === UserRole.STORE) {
    // Store can peek at customer SMS + admin↔driver chatter
    const out: MessageChannel[] = ['customer'];
    if (hasDriver) out.push('dispatch');
    return out;
  }
  // admin-tier: option B keeps Customer as a main tab, so drill-in only
  // exposes the one thread they're excluded from — driver↔store.
  return hasStore && hasDriver ? ['driver_store'] : [];
};

export interface ConversationMessage {
  id: string;
  orderId: string;
  channel: MessageChannel;
  body: string;
  imageUrl?: string;
  timestamp: number;
  senderId: string;
  senderName: string;
  senderRole: UserRole | 'customer';
  // Set when a dispatcher edits a message in-app. `editedAt` stamps when.
  edited?: boolean;
  editedAt?: number;
  // Set only when a moderator (dispatch/admin) edits SOMEONE ELSE's message;
  // the bubble renders "edited by <name>". A sender editing their OWN message
  // leaves these unset (rules restrict a sender's update to body/edited/editedAt).
  editedById?: string;
  editedByName?: string;
  // Soft-delete (moderator tombstone). A dispatcher/admin deleting someone
  // else's message flags it deleted and stamps who/when; the bubble renders
  // as "🚫 Deleted by <name>". A sender deleting their OWN message hard-deletes
  // the doc instead (rules forbid a sender soft-delete), so these stay unset.
  deleted?: boolean;
  deletedById?: string;
  deletedByName?: string;
  deletedAt?: number;
  // WhatsApp-style reactions: emoji → the user ids who picked it. One reaction
  // per user per message. Internal-only — a customer never sees them. Written
  // via FieldPath('reactions', emoji) updates so keys empty cleanly (mirrors
  // the group-room reactions contract).
  reactions?: Record<string, string[]>;
  // WolfChat parity (FEATURES.chatParity): reply / quote. OPTIONAL and
  // client-only for now — the `messages` table has no reply columns yet (rooms
  // got theirs in 0056), so these are populated only if/when the mirror
  // migration named in services/conversationReply.ts is applied. The quote card
  // renders off `replySnapshot` whenever a row carries one.
  replyToId?: string;
  replySnapshot?: RoomReplySnapshot;
}

// --- Group rooms (WhatsApp-style) -------------------------------------------
// A named group chat any app users can be added to. Created + moderated by
// dispatch/admin. Lives at `rooms/{roomId}` with messages under
// `rooms/{roomId}/messages/{messageId}`.
export interface ChatRoom {
  id: string;
  name: string;
  createdBy: string;
  createdByName: string;
  createdAt: number;
  memberIds: string[];
  // Members who can rename, add/remove people, and delete the room. The
  // creator is always an admin; more can be granted later.
  adminIds: string[];
  // Denormalized preview for the rooms list (WhatsApp-style newest-first).
  lastMessageAt?: number;
  lastMessageBody?: string;
  lastMessageSenderName?: string;
  // Sender's uid, so unread logic can skip rooms whose newest message is the
  // viewer's OWN — without it, sending from your phone flashed "1 unread" on
  // your other devices until the sender-receipt synced back.
  lastMessageSenderId?: string;
  // WhatsApp-style receipts, kept on the ROOM (not per message) so a member
  // catching up on 200 messages costs one write instead of 200.
  //   deliveredAt[uid] — newest message timestamp that reached uid's session
  //                      (their app was open anywhere; see AppContext).
  //   readAt[uid]      — newest message timestamp uid actually had on screen
  //                      in the room.
  // A message is ✓✓ once every OTHER member's deliveredAt covers it, and blue
  // once every other member's readAt does (services/roomReceipts.ts).
  deliveredAt?: Record<string, number>;
  readAt?: Record<string, number>;
}

// An order referenced from a group message via the `@` picker. The body keeps
// the human-readable `@<orderNumber>` token so the message still reads as plain
// text (mobile renders it that way); this array carries the `orderId` so a click
// resolves to the RIGHT doc even when the reader can't see it in their own
// `orders` list — dispatch sessions are scoped (services/orderScope.ts), so the
// mentioner and the reader don't always share visibility.
export interface OrderMention {
  orderId: string;
  orderNumber: string;
}

// ── WolfChat parity (WOLFCHAT_CONTRACT.md) ──────────────────────────────────
// Added VERBATIM to wolfdriver-mobile/types/index.ts as well — the two files
// must stay identical. Every addition is OPTIONAL: the columns behind them
// arrive with migrations 0056-0062, and a client reading a database that
// predates them simply sees `undefined`.
export type RoomMessageKind = 'user' | 'system';
export interface RoomSystemEvent { event: 'member_added' | 'member_removed' | 'member_left' | 'room_renamed'; actor?: { id: string; name: string }; target?: { id: string; name: string }; }
export interface RoomReplySnapshot { senderName: string; body: string; deleted?: boolean; hasAttachment?: boolean; }
export interface RoomMention { kind: 'user' | 'all' | 'order'; userId?: string; orderId?: string; orderNumber?: string; label?: string; }
export interface RoomAttachment { id: string; messageId: string; roomId: string; kind: 'image' | 'file'; mime: string; byteSize: number; originalPath: string; displayPath?: string; thumbPath?: string; width?: number; height?: number; fileName?: string; caption?: string; displayUrl?: string; sortOrder: number; }
// expiresAt: WhatsApp pins expire (Umer, 2026-09-14 FINAL #6 — 24 h / 7 d / 30 d,
// default 7 d) and a room holds at most 3 live ones; both are enforced by
// migration 0056. Readers must ignore a pin whose expiresAt has passed.
export interface RoomPin { roomId: string; messageId: string; pinnedById?: string; pinnedByName: string; pinnedAt: number; expiresAt?: number; }
export interface LinkPreview { urlHash: string; url: string; status: 'pending' | 'ok' | 'failed' | 'blocked'; title?: string; description?: string; siteName?: string; imageUrl?: string; }

export interface RoomMessage {
  id: string;
  roomId: string;
  body: string;
  imageUrl?: string;
  // Phase 5 (chatMediaV2): when the image lives in the chat-media bucket,
  // imageUrl is the signed DISPLAY url; these carry the thumbnail url and the
  // canonical original object key. Absent on base64/legacy rows and on mobile
  // rows until mobile ports too.
  imageThumbUrl?: string;
  imagePath?: string;
  timestamp: number;
  senderId: string;
  senderName: string;
  senderRole: UserRole;
  // Orders referenced by `@` tokens in `body`. Absent on every message written
  // before mentions shipped, and on plain-typed order numbers.
  mentions?: OrderMention[];
  // WhatsApp-style reactions: emoji → the user ids who picked it. One
  // reaction per user per message — reacting again with a different emoji
  // moves the id (services/roomActions.setMessageReaction). The key is absent
  // entirely once its last reactor removes theirs, so an empty map never
  // lingers on the doc.
  reactions?: Record<string, string[]>;
  edited?: boolean;
  editedAt?: number;
  // Soft-delete tombstone — the doc stays (body/image cleared) so the thread
  // shows WHO removed it (senders delete their own; room admins + app
  // admins/super-admins can delete anyone's).
  deleted?: boolean;
  deletedAt?: number;
  deletedById?: string;
  deletedByName?: string;
  // ── WolfChat parity additions (2026-09-14) ────────────────────────────────
  // All optional and additive: a row written by a pre-parity client, or read
  // before migration 0056 is applied, simply has none of them. See
  // WOLFCHAT_CONTRACT.md — these names are frozen and must stay byte-identical
  // to wolfdriver-mobile/types/index.ts.
  kind?: RoomMessageKind;
  systemEvent?: RoomSystemEvent;
  replyToId?: string;
  replySnapshot?: RoomReplySnapshot;
  clientId?: string;
  hasAttachment?: boolean;
  attachments?: RoomAttachment[];
  /** `@user` / `@all` / `@order` rows. `mentions` above stays the legacy
   *  order-only jsonb so flag-off clients keep resolving their chips. */
  userMentions?: RoomMention[];
  links?: LinkPreview[];
}

export interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info';
  /** Bold first line (an alert's title, e.g. the group or sender name). */
  title?: string;
  /** Present → the toast is a button: tapping it goes where the event lives. */
  onClick?: () => void;
}

export enum WarehouseHistoryAction {
  PRODUCT_CREATED = 'product_created',
  STOCK_EDITED = 'stock_edited',
  CSV_IMPORT_CREATED = 'csv_import_created',
  CSV_IMPORT_UPDATED = 'csv_import_updated',
  STOCK_OUT_TO_STORE = 'stock_out_to_store',
  STOCK_IN_FROM_STORE = 'stock_in_from_store',
  STOCK_OUT_TO_DRIVER = 'stock_out_to_driver',
  STOCK_IN_FROM_DRIVER = 'stock_in_from_driver',
  STOCK_RESET = 'stock_reset',
  PRODUCT_DELETED = 'product_deleted',
  USER_CREATED = 'user_created',
  USER_DELETED = 'user_deleted',
  USER_UPDATED = 'user_updated',
  PASSWORD_RESET = 'password_reset',
  PASSWORD_CHANGED = 'password_changed',
  TRANSACTION_CREATED = 'transaction_created',
  TRANSACTION_STATUS_CHANGED = 'transaction_status_changed',
  TRANSACTION_DELETED = 'transaction_deleted',
  SYSTEM_CLEARED = 'system_cleared',
  DAY_ZERO = 'day_zero',
}

// Movement types for inventory tracking (maps to spec's 6 movement types)
export enum MovementType {
  WAREHOUSE_TO_STORE = 'warehouse_to_store',
  STORE_TO_DRIVER = 'store_to_driver',
  DELIVERED = 'delivered',
  RETURNED_TO_STORE = 'returned_to_store',
  TOP_UP = 'top_up',               // warehouse → driver direct
  DRIVER_TO_DRIVER = 'driver_to_driver',
  STORE_TO_WAREHOUSE = 'store_to_warehouse',
  DRIVER_TO_WAREHOUSE = 'driver_to_warehouse',
  DRIVER_TO_STORE = 'driver_to_store',
  DRIVER_DIRECT = 'driver_direct',
  DELIVERY_REVERSED = 'delivery_reversed',   // compensating movement when admin reverses a delivery
}

// Inventory Tracker: immutable movement ledger record
export interface Movement {
  id: string;
  date: string;                  // Business day: YYYY-MM-DD
  productId: string;
  productName: string;
  type: MovementType;
  fromEntity: string;            // Human-readable source (store name, driver name, "Warehouse")
  toEntity: string;              // Human-readable destination
  storeId: string | null;        // Which store this movement is associated with
  driverId: string | null;       // The driver involved (for transfers, the FROM driver)
  toDriverId: string | null;     // Only for driver-to-driver transfers
  orderId: string | null;        // Links to order, if applicable
  orderNumber: string | null;    // Human-readable order number
  qty: number;                   // Always positive
  reason: string | null;         // Why this movement happened (vehicle_breakdown, load_balancing, etc.)
  timestamp: number;             // Exact time of the movement
  // Which route the stock took (migration 0056): 'direct_from_warehouse'
  // when the packs were built on the Warehouse page and sent straight to the
  // store / driver; null/absent = the normal Packaging House → Inventory Ops
  // route. `runId` links back to the production run either way when known.
  route?: 'direct_from_warehouse' | null;
  runId?: string | null;
}

export interface WarehouseHistoryEntry {
  id: string;
  productId: string;
  productName: string;
  action: WarehouseHistoryAction;
  timestamp: number;
  performedBy: {
    userId: string;
    userName: string;
    role: UserRole;
  };
  quantityChange: number;
  quantityBefore: number;
  quantityAfter: number;
  storeName?: string;
  transactionId?: string;
  description: string;
  // Inventory tracker enrichment fields
  movementType?: MovementType;
  fromEntity?: string;  // e.g. 'warehouse', storeId, driverId
  toEntity?: string;    // e.g. 'warehouse', storeId, driverId
  driverId?: string;
  toDriverId?: string;
  storeId?: string;
  orderId?: string;
}

export enum StoreHistoryAction {
  STOCK_RECEIVED = 'stock_received',
  STOCK_RETURNED = 'stock_returned',
  ORDER_ASSIGNED = 'order_assigned',
  ORDER_UNASSIGNED = 'order_unassigned',
  ORDER_DELIVERED = 'order_delivered',
  DELIVERY_REVERSED = 'delivery_reversed',
  CASH_COLLECTED = 'cash_collected',
  CASH_TURNED_IN = 'cash_turned_in',
  ORDER_CREATED = 'order_created',
  ORDER_DELETED = 'order_deleted',
  ORDER_EDITED = 'order_edited',
  DRIVER_ASSIGNED = 'driver_assigned',
  DRIVER_CHANGED = 'driver_changed',
  DRIVER_UNASSIGNED = 'driver_unassigned',
  PICKUP_CONFIRMED = 'pickup_confirmed',
  PICKUP_OVERRIDE = 'pickup_override',
  RETURN_INITIATED = 'return_initiated',
  RETURN_COMPLETED = 'return_completed',
  MESSAGE_SENT = 'message_sent',
  SMS_SENT = 'sms_sent',
  CUSTOMER_CALLED = 'customer_called',
  TEMP_PACKER_ASSIGNED = 'temp_packer_assigned',
  TEMP_PACKER_REMOVED = 'temp_packer_removed',
}

export interface StoreHistoryEntry {
  id: string;
  storeId: string;
  storeName: string;
  action: StoreHistoryAction;
  timestamp: number;
  productId?: string;
  productName?: string;
  quantityChange?: number;
  orderId?: string;
  orderNumber?: string;
  cashAmount?: number;
  performedBy: {
    userId: string;
    userName: string;
    role: UserRole;
  };
  description: string;
  // Inventory tracker enrichment fields
  movementType?: MovementType;
  fromEntity?: string;
  toEntity?: string;
  driverId?: string;
  toDriverId?: string;
}

export enum DriverHistoryAction {
  STOCK_RECEIVED = 'stock_received',
  STOCK_RETURNED = 'stock_returned',
  ORDER_ASSIGNED = 'order_assigned',
  ORDER_UNASSIGNED = 'order_unassigned',
  ORDER_DELIVERED = 'order_delivered',
  DELIVERY_REVERSED = 'delivery_reversed',
  ORDER_EDITED = 'order_edited',
  CASH_COLLECTED = 'cash_collected',
  CASH_TURNED_IN = 'cash_turned_in',
  DRIVER_ASSIGNED = 'driver_assigned',
  DRIVER_CHANGED = 'driver_changed',
  DRIVER_UNASSIGNED = 'driver_unassigned',
  TRANSFER_INITIATED = 'transfer_initiated',
  TRANSFER_APPROVED = 'transfer_approved',
  TRANSFER_COMPLETED = 'transfer_completed',
  RETURN_INITIATED = 'return_initiated',
  RETURN_COMPLETED = 'return_completed',
  PICKUP_CONFIRMED = 'pickup_confirmed',
  MESSAGE_SENT = 'message_sent',
  SMS_SENT = 'sms_sent',
  CUSTOMER_CALLED = 'customer_called',
  TEMP_PACKER_ASSIGNED = 'temp_packer_assigned',
  TEMP_PACKER_REMOVED = 'temp_packer_removed',
}

export interface DriverHistoryEntry {
  id: string;
  driverId: string;
  driverName: string;
  action: DriverHistoryAction;
  timestamp: number;
  productId?: string;
  productName?: string;
  quantityChange?: number;
  orderId?: string;
  orderNumber?: string;
  cashAmount?: number;
  performedBy: {
    userId: string;
    userName: string;
    role: UserRole;
  };
  description: string;
  // Inventory tracker enrichment fields
  movementType?: MovementType;
  fromEntity?: string;
  toEntity?: string;
  storeId?: string;
  toDriverId?: string;
}

// ─── Warehouse Manufacturing Flow ──────────────────────────────────────────
// The bulk-arrival → SKU-transformation flow. Three roles:
//   Warehouse Manager (WM) — logs Inbound Lots, assigns bulk to Workers,
//     watches everything. Read-only on execution.
//   Worker — receives assignments, runs the actual production. Holds their
//     own bulk / crumbs / leftovers (tagged by source lot). Can mix crumbs
//     from multiple lots inline on any run — provenance stored on the run.
//   Packager — QA gate. Confirms finished products from a Worker. Only
//     the confirm event credits Product.warehouseStock.
//
// Store bundling is NOT part of this flow — it stays in the existing
// InventoryDispatch page and is Dispatch/Admin's responsibility.

export enum LotStatus {
  RECEIVED = 'received',                    // Just logged, no assignments yet
  IN_PRODUCTION = 'in_production',          // At least one grams-out event has happened
  DEPLETED = 'depleted',                    // remainingGrams === 0, closed cleanly
  CLOSED_WITH_VARIANCE = 'closed_variance', // WM closed with accepted variance
}

// InboundLot: a bulk shipment logged by the Warehouse Manager.
// `remainingGrams` = what still sits with WM (not yet assigned to any Worker).
// Grams held by Workers are tracked via LotAssignment + ProductionRun, not
// on this doc. Traceability is preserved on ProductionRun.inputSources.
// A lot originates either from a physical truck (WM logged intake) or from
// a worker deriving a new material out of their assigned bulk (see
// splitBulkIntoNewMaterial). Legacy lots without `origin` are treated as 'truck'.
export type LotOrigin = 'truck' | 'worker_derived';

export interface InboundLot {
  id: string;
  name: string;                       // e.g. "ABC Weed — Lot 12"
  totalGrams: number;                 // As received off the truck
  adjustedGrams?: number;             // Σ production-variance adjustments (0123) — received = totalGrams − adjustedGrams
  remainingGrams: number;             // Live balance with WM (unassigned)
  // Arrives retail-ready — vapes, cartridges and anything else already in its
  // packaging (Umer, 2026-09-15). The Packaging House hand-off is hidden for
  // such a lot; it goes straight to a store, a driver, or the Storage House.
  skipsPackaging?: boolean;
  status: LotStatus;
  receivedAt: number;
  receivedBy: {
    userId: string;
    userName: string;
    role: UserRole;
  };
  notes?: string;
  // How the shipment physically arrived when logged as packs ("10 × ½ oz").
  // Display + audit only — totalGrams stays the canonical quantity and all
  // downstream accounting ignores these.
  packCount?: number;
  packSizeGrams?: number;
  // Count-based lots (Quantity / Boxes intake): the material is never weighed
  // — totalGrams/remainingGrams HOLD THE COUNT so all downstream math flows
  // unchanged, and `countUnit` ("bundles", "cartons", …) flips every display
  // from grams to counts. boxCount/unitsPerBox are the Boxes-mode breakdown
  // (display + audit only, like packCount above).
  countUnit?: string;
  boxCount?: number;
  unitsPerBox?: number;
  closedAt?: number;
  closedBy?: {
    userId: string;
    userName: string;
    role: UserRole;
  };
  varianceGrams?: number;             // Accepted variance at close
  // Set on worker-derived lots so we can trace lineage back. Two flavors:
  //   1. Worker Split: parents.length === 1, no fromProductionRunId
  //      (worker deliberately named + split a chunk of one lot)
  //   2. Mixed-run leftover: fromProductionRunId set, parents = all sources
  //      (auto-created when a mixed-source run had leftover material — those
  //      crumbs become a new named material named after the output product)
  origin?: LotOrigin;
  derivedFrom?: {
    // `grams` is how much material was pulled from THAT specific parent to
    // make the derived lot. Older derived-lot docs written before this field
    // was introduced won't have it; readers should fall back to a
    // best-effort estimate (proportional to the spawn run's inputSources, or
    // totalGrams / parents.length as a last resort).
    parents: Array<{ id: string; name: string; grams?: number }>;
    workerId: string;
    workerName: string;
    // Packing House the material was derived in (0080) — the pool its
    // leftover goes back to. Absent on legacy per-person lots.
    houseId?: string;
    houseName?: string;
    createdAt: number;
    fromProductionRunId?: string;
    fromProductName?: string;
  };
}

export enum AssignmentStatus {
  ACTIVE = 'active',                  // Worker is holding + processing
  CLOSED = 'closed',                  // Worker returned everything to WM
}

// LotAssignment: WM grants N grams of a specific lot to a specific Worker.
// The Worker now physically + systemically holds that bulk. `remainingGrams`
// tracks the unconsumed bulk portion on the Worker's table (does not include
// crumbs the Worker has generated — crumbs are tracked separately via runs).
export interface LotAssignment {
  id: string;
  lotId: string;
  lotName: string;                    // Denormalised
  // The HOLDER. Legacy grant: the person. House grant (houseId set): these
  // repeat the house's id and name — there is no single person to name, and
  // every "on X's table" line then prints the house.
  workerId: string;
  workerName: string;                 // Denormalised
  // Packing House pool the grant went to (migration 0080). Absent = legacy
  // per-person grant.
  houseId?: string;
  houseName?: string;
  grantedGrams: number;
  remainingGrams: number;             // Bulk not yet consumed by any run
  // Denormalised from the lot: present when the numbers are COUNTS, so the
  // Packaging House renders "20 bundles" without a lot lookup.
  countUnit?: string;
  // The products the Warehouse named at hand-off (0117): the batch offers them first; any other
  // product is allowed after a recorded warning.
  targetProductIds?: string[];
  status: AssignmentStatus;
  assignedAt: number;
  assignedBy: {
    userId: string;
    userName: string;
    role: UserRole;
  };
  closedAt?: number;
  notes?: string;
}

// ProductionOutput: what a Worker produced in a single run.
export interface ProductionOutput {
  productId: string;
  productName: string;
  qty: number;                        // Units produced
  unitGrams: number;                  // Grams per unit
}

// InputSource: one leg of a Production Run's input — where a portion of the
// consumed grams came from. A run can mix multiple sources across lots.
// `source` distinguishes bulk from an assignment vs. crumbs the Worker had
// accumulated from prior runs (attributed to a specific lot). Provenance
// chain is preserved forever via lotId.
export type InputSource =
  | { kind: 'bulk'; lotId: string; lotName: string; assignmentId: string; grams: number }
  // 'bulk_direct' = Admin/SA/WM taking bulk from a lot's on-shelf remainder
  // without going through an assignment. No worker gate; the lot's
  // remainingGrams is debited directly by the transaction.
  | { kind: 'bulk_direct'; lotId: string; lotName: string; grams: number }
  | { kind: 'crumbs'; lotId: string; lotName: string; grams: number };

// ProductionRun: a single execution by a Worker. Consumes from any mix of
// their held inputs (bulk assignments + accumulated crumbs). Outputs are
// PENDING_PACKAGER until a Packager confirms — only then does
// Product.warehouseStock get credited.
// Run lifecycle:
//   PENDING_PACKAGER — Worker submitted, awaiting packager review ("unpacked")
//   PACKED           — Packager confirmed count/quality, sitting off to the
//                      side, not yet in warehouse inventory
//   CONFIRMED        — Packed items transferred into Product.warehouseStock
//                      (this is the moment finished goods become visible to
//                      the rest of the app)
//   REJECTED         — Packager sent back to worker for edit
export enum RunStatus {
  PENDING_PACKAGER = 'pending_packager',
  PACKED = 'packed',
  CONFIRMED = 'confirmed',
  REJECTED = 'rejected',
}

export interface ProductionRun {
  id: string;
  // The packager who made the batch — always a person (traceability).
  workerId: string;
  workerName: string;
  // Packing House pool the batch drew from (migration 0080). Absent on
  // legacy per-person runs and on direct-assign runs.
  houseId?: string;
  houseName?: string;
  performedAt: number;
  performedBy: {
    userId: string;
    userName: string;
    role: UserRole;
  };
  inputSources: InputSource[];        // Multi-lot input mix (bulk + crumbs)
  totalGramsConsumed: number;         // Sum of inputSources.grams (denormalised)
  outputs: ProductionOutput[];        // Products made (PENDING until confirmed)
  crumbsGrams: number;                // Total crumbs generated by this run
  wasteGrams: number;                 // Total waste (write-off)
  variance: number;                   // totalGramsConsumed − Σ(qty×unitGrams) − crumbs − waste
  status: RunStatus;
  confirmedAt?: number;                 // Packager confirmed (PACKED)
  confirmedBy?: {
    userId: string;
    userName: string;
    role: UserRole;
  };
  transferredAt?: number;               // Transferred to warehouseStock (CONFIRMED)
  transferredBy?: {
    userId: string;
    userName: string;
    role: UserRole;
  };
  rejectionReason?: string;
  notes?: string;
  // Direct warehouse assignment (migration 0056): this run was created by
  // "Send to Store / Driver" on the Warehouse page — packs built as part of
  // the assignment, run confirmed on the spot, stock already on its way as
  // the linked transaction. Absent on Packaging House batches.
  directAssign?: DirectAssignInfo;
  // Made from a claim (0117): the claim the material came off. `variance` is then used − packed
  // (+ = less packed than used, − = more): kept, never refused, shown to admins only.
  claimId?: string;
  // Every warning the packager accepted while making or editing the batch (0117).
  warnings?: RunWarning[];
  // Production variance (0123): the lot was adjusted by this many grams (or counts) to what was packed —
  // + packed more, − packed less; absent = not adjusted.
  lotAdjustGrams?: number;
}

// A warning the packager read and accepted (0117) — e.g. a product the hand-off did not name.
export interface RunWarning {
  code: 'off_target' | string;
  text: string;
  acceptedBy: string;      // user id
  acceptedByName?: string;
  acceptedAt: number;      // Unix ms
}

// PackingClaim (0117): part of a house grant on ONE person's table. Open to take on a grant =
// its remainingGrams − the active claims on it; a batch debits the claim and the grant together.
export type PackingClaimStatus = 'active' | 'returned' | 'done';
export interface PackingClaim {
  id: string;
  houseId: string;
  assignmentId: string;
  lotId: string;
  lotName: string;
  workerId: string;
  workerName: string;
  gramsClaimed: number;     // everything ever taken onto this claim
  gramsRemaining: number;   // still on the table
  countUnit?: string;
  status: PackingClaimStatus;
  releasedReason?: 'returned' | 'expired' | 'used_up';
  claimedAt: number;
  lastActivityAt: number;
  releasedAt?: number;
}

export interface DirectAssignInfo {
  // 'storage' = packed straight onto the finished-goods shelf (the Storage
  // House). It has no transaction, because nothing left the building and
  // there is nobody to confirm receipt.
  destinationType: 'store' | 'driver' | 'storage';
  destinationId: string;
  destinationName: string;
  transactionId: string | null;
  lotId: string;
  lotName: string;
}

// LotReturn: OPTIONAL end-of-shift action where a Worker hands unused bulk
// and/or accumulated crumbs back to WM. Reduces the Worker's holdings and
// increases the lot's `remainingGrams` (for bulk) or is written off (for
// waste returns). Most workflow doesn't need this — the Worker just uses
// the material in the next run — but shift handoffs need it.
export enum ReturnKind {
  BULK = 'bulk',                      // Returning unused assignment grams
  CRUMBS = 'crumbs',                  // Returning accumulated crumbs
}

// Return lifecycle:
//   PENDING   — worker recorded the return; material effectively "with the
//               WM's desk" but not yet credited back to the lot.
//   CONFIRMED — WM confirmed receipt; lot's remainingGrams was credited.
//   REJECTED  — WM refused to accept (miscount, wrong lot, etc); worker
//               keeps the material.
// Legacy returns without status field default to CONFIRMED for backward compat.
export enum ReturnStatus {
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  REJECTED = 'rejected',
}

export interface LotReturn {
  id: string;
  // The packager who recorded the return — always a person.
  workerId: string;
  workerName: string;
  // Packing House pool the material came back from (migration 0080).
  houseId?: string;
  houseName?: string;
  lotId: string;
  lotName: string;
  kind: ReturnKind;
  grams: number;
  returnedAt: number;
  notes?: string;
  status?: ReturnStatus;
  confirmedAt?: number;
  confirmedBy?: {
    userId: string;
    userName: string;
    role: UserRole;
  };
  rejectionReason?: string;
}

export interface AppState {
  currentUser: User | null;
  users: User[];
  products: Product[];
  storeStocks: StoreStock[];
  driverStocks: DriverStock[];
  transactions: StockTransaction[];
  // Role-scoped for DISPATCH sessions (each dispatch sees own + unclaimed
  // orders — see services/orderScope.ts). All other roles get the raw array.
  orders: Order[];
  // Unscoped escape hatch for admin tooling. Optional so test mocks that
  // build partial AppState objects don't have to provide it.
  allOrders?: Order[];
  twilioNumbers: TwilioNumber[];
  // Temporary store managers (store × driver). Empty in Firestore mode —
  // there the legacy User.tempPackerDriverId is the only slot.
  storeTempManagers: StoreTempManager[];
  // Packing Houses + their members (0080). Empty in Firestore mode — there
  // the Packaging House is still one person per grant.
  packingHouses: PackingHouse[];
  packingHouseMembers: PackingHouseMember[];
  // Group rooms (WhatsApp-style) the current user is a member of — fed by a
  // single `rooms` where(memberIds array-contains uid) listener.
  rooms: ChatRoom[];
  inboundLots: InboundLot[];
  lotAssignments: LotAssignment[];
  productionRuns: ProductionRun[];
  lotReturns: LotReturn[];
  // Claims (0117): every claim of every house (the page filters). `packingClaimsReady` is true once
  // the table answered — before 0117 it never does and the Packaging House keeps the shared pile.
  packingClaims: PackingClaim[];
  packingClaimsReady: boolean;

  loading: boolean;
  /** A session exists but the profile row is not read yet (in flight, or the
   *  read failed and is being retried). The app shows Reconnecting, never Login. */
  sessionPending: boolean;
  retrySession: () => void;
  ordersLoaded: boolean;
  toasts: Toast[];
  showToast: (message: string, type?: 'success' | 'error' | 'info', opts?: { title?: string; onClick?: () => void }) => void;
  removeToast: (id: string) => void;
  setCurrentUser: (user: User) => void;

  // Auth
  login: (email: string, pass: string) => Promise<boolean>;
  signup: (email: string, pass: string, name: string, phone: string) => Promise<boolean>;
  logout: () => void;
  adminCreateUser: (email: string, pass: string, name: string, role: UserRole, phone?: string, storeAccess?: boolean) => Promise<{success: boolean, error?: string}>;
  updateUser: (user: User) => void;
  deleteUser: (id: string) => void;
  adminResetUserPassword: (userId: string, tempPass: string) => Promise<boolean>;
  changePassword: (newPass: string, currentPass?: string) => Promise<void>;

  // Data
  addProduct: (product: Product, source?: 'csv' | 'manual') => void;
  updateProduct: (product: Product, source?: 'csv' | 'manual') => void;
  deleteProduct: (id: string) => boolean;
  createTransaction: (transaction: StockTransaction) => void;
  // `receipt` is only passed by a store / driver whose counts differed from
  // what was sent (see TransactionReceipt); omitted = received in full.
  updateTransactionStatus: (id: string, status: TransactionStatus, receipt?: TransactionReceipt) => void;
  deleteTransaction: (id: string) => void;
  createOrder: (order: Order) => void;
  updateOrder: (order: Order) => void;
  /** Field-level write of exactly `patch` (e.g. the priority pill); assignment columns keep their own helpers. */
  patchOrder: (orderId: string, patch: Partial<Order>) => Promise<void>;
  // Edit an assigned order's own details (customer, address, payment, notes) — one conditional write, no stock; answers the changed field names.
  updateOrderDetails: (order: Order) => Promise<string[]>;
  /** Soft delete (0097): a Pending order becomes Cancelled — kept, hidden from active lists, its card follows. */
  cancelOrder: (id: string) => Promise<void>;
  /** Put back a cancelled order: Pending again (its card returns too). */
  restoreOrder: (id: string) => Promise<void>;
  assignOrder: (orderId: string, storeId: string) => void;
  assignOrderToDriver: (orderId: string, driverId: string) => void;
  assignDriver: (orderId: string, driverId: string) => void;
  unassignOrder: (orderId: string) => void;
  unassignStore: (orderId: string) => void;
  initiateDriverReturn: (orderId: string) => void;
  reverseOrder: (orderId: string, targetStatus: OrderStatus) => void;

  // Helpers
  getStoreStock: (storeId: string, productId: string) => number;
  getDriverStock: (driverId: string, productId: string) => number;
  getStoreCashBalance: (storeId: string) => number;
  getDriverCashBalance: (driverId: string) => number;
  confirmStorePickup: (orderId: string) => void;
  /** 0105: the office marks a CONFIRMED order with its store and driver delivered (services/orderDelivery). */
  markOrderDelivered: (orderId: string) => Promise<boolean>;

  // Driver return confirmation (driver says "I'm at the store, returning stock")
  driverConfirmReturn: (orderId: string) => void;
  /** The office takes an order back from its driver at once — the goods are back at the store (2026-10-02). */
  officeTakeBack?: (orderId: string) => Promise<boolean>;

  // Driver-to-driver transfer
  initiateDriverTransfer: (orderId: string, toDriverId: string) => void;
  approveDriverTransfer: (orderId: string, driverId: string) => void;

  // Driver as temp packer
  assignTempPacker: (storeId: string, driverId: string) => void;
  /** Remove ONE manager from a store. Without `driverId` (legacy callers)
   *  every manager of the store is removed. */
  removeTempPacker: (storeId: string, driverId?: string) => void;

  // Packing Houses (migration 0080). Supabase only — no-ops in Firestore mode.
  createPackingHouse: (input: { name: string; address?: string; notes?: string }) => Promise<string | null>;
  updatePackingHouse: (house: PackingHouse) => Promise<void>;
  addPackingHouseMember: (houseId: string, userId: string) => Promise<void>;
  removePackingHouseMember: (houseId: string, userId: string) => Promise<void>;

  // Notification / read tracking
  markOrderRead: (orderId: string) => void;
  markChatRead: (orderId: string, channel: MessageChannel) => void;
  markChatReadBatch: (orderId: string, channels: MessageChannel[]) => void;
  getUnreadChatCounts: (orderId: string) => { customer: number; store: number; dispatch: number; total: number };
  /** Mark a nav/tab as "opened" now — writes users/{uid}.lastOpenedAt.{tab},
   *  clearing the badge count for that tab. */
  markTabOpened: (tab: 'deliveries' | 'pickups' | 'transfers' | 'store' | 'orders' | 'messages') => Promise<void>;

  // Lazy-loading for orders/transactions (date-range pagination)
  loadOrdersByDateRange: (startDate: number, endDate: number) => Promise<Order[]>;
  loadTransactionsByDateRange: (startDate: number, endDate: number) => Promise<StockTransaction[]>;
  ordersDateRange: { start: number; end: number } | null;
  transactionsDateRange: { start: number; end: number } | null;

  // Twilio number pool
  addTwilioNumber: (number: string, label?: string) => Promise<void>;
  assignTwilioNumberToDriver: (numberId: string, driverId: string | null) => Promise<void>;
  setDefaultTwilioNumber: (numberId: string) => Promise<void>;
  removeTwilioNumber: (numberId: string) => Promise<void>;

  // System
  resetData: () => void;
  seedDatabase: () => Promise<void>;
  sendSMS: (to: string, body: string) => Promise<boolean>;
  sendCustomerMessage: (orderId: string, messageBody: string) => Promise<boolean>;
  callCustomer: (orderId: string) => Promise<boolean>;
  clearSystemData: () => Promise<void>;
  seedWarehouseHistory: () => Promise<void>;
  seedStoreHistory: () => Promise<void>;

  // Day Zero
  dayZeroTimestamp: number | null;
  performDayZero: () => Promise<void>;
  resetAllStoreStock: () => Promise<void>;

  // Warehouse Manufacturing (WM + Worker + Packager flow)
  createInboundLot: (input: {
    name: string;
    totalGrams: number;
    notes?: string;
    // Present when the WM logged the shipment as packs (count × size).
    packCount?: number;
    packSizeGrams?: number;
    // Present on count-based intake (Quantity / Boxes): totalGrams is the
    // COUNT and countUnit names it; boxCount/unitsPerBox = Boxes breakdown.
    countUnit?: string;
    boxCount?: number;
    unitsPerBox?: number;
    skipsPackaging?: boolean;
  }) => Promise<string | null>;
  // Hand material to a Packing House (`houseId`, 0080) or — legacy /
  // Firestore mode — to one person (`workerId`). Exactly one of the two.
  assignBulkToWorker: (input: {
    lotId: string;
    workerId?: string;
    houseId?: string;
    grams: number;
    notes?: string;
    // The products to make from it (0117) — offered first in the batch.
    targetProductIds?: string[];
  }) => Promise<string | null>;
  // Direct warehouse assignment (migration 0056): packs are built from a lot
  // on the shelf and sent straight to a store or driver in ONE atomic call —
  // lot debited, run confirmed, warehouse stock credited, transfer pending
  // the receiver's confirmation. Resolves to the transaction id.
  directAssignFromWarehouse: (input: {
    lotId: string;
    destinationType: 'store' | 'driver';
    destinationId: string;
    outputs: ProductionOutput[];
    wasteGrams: number;
    notes?: string;
  }) => Promise<string | null>;
  // Send packs a store / driver refused to a different destination (0057).
  // The packs already exist; this only creates the transfer, and the server
  // refuses anything past what the batch actually made.
  // Pack a lot straight onto the Storage House shelf — no destination, no
  // receiving step, the packs simply become available stock.
  packIntoStorageHouse: (input: {
    lotId: string;
    outputs: ProductionOutput[];
    wasteGrams: number;
    notes?: string;
  }) => Promise<string | null>;
  resendDirectPacks: (input: {
    runId: string;
    destinationType: 'store' | 'driver';
    destinationId: string;
    items: { productId: string; quantity: number }[];
  }) => Promise<string | null>;
  createProductionRun: (input: {
    inputSources: InputSource[];
    outputs: ProductionOutput[];
    crumbsGrams: number;
    wasteGrams: number;
    notes?: string;
    actingAsWorkerId?: string;
    // The Packing House pool the batch draws from (0080). Required for
    // material that was handed to a house; the run is stamped with it.
    houseId?: string;
  }) => Promise<string | null>;
  confirmProductionRun: (runId: string) => Promise<void>;
  // Transitions a PACKED run into CONFIRMED and credits Product.warehouseStock.
  // The physical parallel: packed items on the QA table get walked over to the
  // warehouse shelves. Only after this step do finished goods appear in the
  // rest of the app's inventory views.
  transferPackedToInventory: (runId: string) => Promise<void>;
  rejectProductionRun: (runId: string, reason: string) => Promise<void>;
  // Worker can edit their own run (recompute outputs/crumbs/waste/notes) if
  // it's PENDING_PACKAGER or REJECTED. Inputs are immutable — bulk was
  // already consumed physically. Edit flips status back to PENDING_PACKAGER.
  updateProductionRun: (runId: string, edits: {
    outputs: ProductionOutput[];
    crumbsGrams: number;
    wasteGrams: number;
    notes?: string;
  }) => Promise<void>;
  // Claims (0117). All three go through one database call each.
  takePackingClaim: (input: { assignmentId: string; grams: number }) => Promise<PackingClaim | null>;
  returnPackingClaim: (input: { claimId: string; grams: number }) => Promise<boolean>;
  /** Give back from my table to the Warehouse (0122): the claim goes down, a pending return is written. */
  givePackingClaimBack: (input: { claimId: string; grams: number; notes?: string }) => Promise<boolean>;
  /** Set only a product's size (unit_amount / unit_kind) — never its stock, price or anything else. */
  setProductUnit: (productId: string, unit: { amount: number; kind: ProductUnitKind }) => Promise<boolean>;
  makeBatchFromClaim: (input: {
    claimId: string;
    usedGrams: number;
    outputs: ProductionOutput[];
    notes?: string;
    warnings?: RunWarning[];
    /** Adjust the lot to what was packed (0123): the difference moves the lot's weight on the spot. */
    adjustLot?: boolean;
  }) => Promise<string | null>;
  editBatchFromClaim: (input: { runId: string; outputs: ProductionOutput[]; notes?: string; warnings?: RunWarning[] }) => Promise<boolean>;
  returnToWarehouse: (input: {
    lotId: string;
    kind: ReturnKind;
    grams: number;
    notes?: string;
    // The Packing House pool the material comes back from (0080).
    houseId?: string;
    actingAsWorkerId?: string;
  }) => Promise<string | null>;
  confirmLotReturn: (returnId: string) => Promise<void>;
  rejectLotReturn: (returnId: string, reason: string) => Promise<void>;
  closeLot: (lotId: string, acceptedVariance: number) => Promise<void>;
  // Worker takes one or more chunks of their assigned bulk and turns them
  // into a new named material (a derived lot). Single source = Split. Two or
  // more = Mix. The new lot appears as fresh bulk on the worker's holdings
  // and traces back to every source via derivedFrom.parents.
  combineIntoNewMaterial: (input: {
    sources: Array<{ sourceAssignmentId: string; grams: number }>;
    newLotName: string;
    notes?: string;
  }) => Promise<string | null>;
  // Derived helpers — computed from state, no writes.
  // Returns per-lot bulk + crumbs balance for one worker.
  // The shared pool of one Packing House (0080): the same ledger as
  // getWorkerHoldings, keyed on the rows' houseId instead of the person.
  getHouseHoldings: (houseId: string) => Array<{
    lotId: string;
    lotName: string;
    bulkGrams: number;
    crumbsGrams: number;
    assignmentId?: string;
  }>;
  getWorkerHoldings: (workerId: string) => Array<{
    lotId: string;
    lotName: string;
    bulkGrams: number;
    crumbsGrams: number;
    assignmentId?: string;
  }>;
}
